// mds-dojo — dijit/form/_FormMixin — FAITHFUL port of references/dijit/form/_FormMixin.js.
// AMD → ESM + declare from the faithful runtime. Mixin for containers of form widgets
// (extract/populate a name→value hash, aggregate valid/incomplete state).
// `kernel.deprecated(...)` calls guarded (mds kernel may not define it).
// Ported here because dijit/Dialog + dijit/TooltipDialog mix it in.
import array from '../../dojo/_base/array';
import declare from '../../dojo/_base/declare-runtime';
import kernel from '../../dojo/_base/kernel';
import lang from '../../dojo/_base/lang';
import on from '../../dojo/on';
import winUtils from '../../dojo/window';

export default declare('dijit.form._FormMixin', null, {
  // summary:
  //		Mixin for containers of form widgets (i.e. widgets that represent a single value
  //		and can be children of a `<form>` node or `dijit/form/Form` widget)

  // state: [readonly] String
  //		Will be "Error" if one or more of the child widgets has an invalid value,
  //		"Incomplete" if not all of the required child widgets are filled in.  Otherwise, "".
  state: '',

  _getDescendantFormWidgets: function (this: any, /*dijit/_WidgetBase[]?*/ children: any) {
    // summary:
    //		Returns all form widget descendants, searching through non-form child widgets like BorderContainer
    var res: any[] = [];
    array.forEach(children || this.getChildren(), function (this: any, child: any) {
      if ('value' in child) {
        res.push(child);
      } else {
        res = res.concat(this._getDescendantFormWidgets(child.getChildren()));
      }
    }, this);
    return res;
  },

  reset: function (this: any) {
    array.forEach(this._getDescendantFormWidgets(), function (widget: any) {
      if (widget.reset) {
        widget.reset();
      }
    });
  },

  validate: function (this: any) {
    // summary:
    //		returns if the form is valid - same as isValid - but
    //		provides a few additional (ui-specific) features:
    var didFocus = false;
    return array.every(array.map(this._getDescendantFormWidgets(), function (widget: any) {
      // Need to set this so that "required" widgets get their
      // state set.
      widget._hasBeenBlurred = true;
      var valid = widget.disabled || !widget.validate || widget.validate();
      if (!valid && !didFocus) {
        // Set focus of the first non-valid widget
        winUtils.scrollIntoView(widget.containerNode || widget.domNode);
        widget.focus();
        didFocus = true;
      }
      return valid;
    }), function (item: any) { return item; });
  },

  setValues: function (this: any, val: any) {
    if (kernel.deprecated) {
      kernel.deprecated(this.declaredClass + "::setValues() is deprecated. Use set('value', val) instead.", '', '2.0');
    }
    return this.set('value', val);
  },
  _setValueAttr: function (this: any, /*Object*/ obj: any) {
    // summary:
    //		Fill in form values from according to an Object (in the format returned by get('value'))

    // generate map from name --> [list of widgets with that name]
    var map: any = {};
    array.forEach(this._getDescendantFormWidgets(), function (widget: any) {
      if (!widget.name) { return; }
      var entry = map[widget.name] || (map[widget.name] = []);
      entry.push(widget);
    });

    for (var name in map) {
      if (!map.hasOwnProperty(name)) {
        continue;
      }
      var widgets = map[name],						// array of widgets w/this name
        values = lang.getObject(name, false, obj);	// list of values for those widgets

      if (values === undefined) {
        continue;
      }
      values = [].concat(values);
      if (typeof widgets[0].checked == 'boolean') {
        // for checkbox/radio, values is a list of which widgets should be checked
        array.forEach(widgets, function (w: any) {
          w.set('value', array.indexOf(values, w._get('value')) != -1);
        });
      } else if (widgets[0].multiple) {
        // it takes an array (e.g. multi-select)
        widgets[0].set('value', values);
      } else {
        // otherwise, values is a list of values to be assigned sequentially to each widget
        array.forEach(widgets, function (w: any, i: any) {
          w.set('value', values[i]);
        });
      }
    }

    // Note: no need to call this._set("value", ...) as the child updates will trigger onChange events
    // which I am monitoring.
  },

  getValues: function (this: any) {
    if (kernel.deprecated) {
      kernel.deprecated(this.declaredClass + "::getValues() is deprecated. Use get('value') instead.", '', '2.0');
    }
    return this.get('value');
  },
  _getValueAttr: function (this: any) {
    // summary:
    //		Returns Object representing form values.   See description of `value` for details.

    // get widget values
    var obj: any = {};
    array.forEach(this._getDescendantFormWidgets(), function (widget: any) {
      var name = widget.name;
      if (!name || widget.disabled) { return; }

      // Single value widget (checkbox, radio, or plain <input> type widget)
      var value = widget.get('value');

      // Store widget's value(s) as a scalar, except for checkboxes which are automatically arrays
      if (typeof widget.checked == 'boolean') {
        if (/Radio/.test(widget.declaredClass)) {
          // radio button
          if (value !== false) {
            lang.setObject(name, value, obj);
          } else {
            // give radio widgets a default of null
            value = lang.getObject(name, false, obj);
            if (value === undefined) {
              lang.setObject(name, null, obj);
            }
          }
        } else {
          // checkbox/toggle button
          var ary = lang.getObject(name, false, obj);
          if (!ary) {
            ary = [];
            lang.setObject(name, ary, obj);
          }
          if (value !== false) {
            ary.push(value);
          }
        }
      } else {
        var prev = lang.getObject(name, false, obj);
        if (typeof prev != 'undefined') {
          if (lang.isArray(prev)) {
            prev.push(value);
          } else {
            lang.setObject(name, [prev, value], obj);
          }
        } else {
          // unique name
          lang.setObject(name, value, obj);
        }
      }
    });
    return obj;
  },

  isValid: function (this: any) {
    // summary:
    //		Returns true if all of the widgets are valid.
    //		Deprecated, will be removed in 2.0.  Use get("state") instead.

    return this.state == '';
  },

  onValidStateChange: function (/*Boolean*/ /*===== isValid =====*/) {
    // summary:
    //		Stub function to connect to if you want to do something
    //		(like disable/enable a submit button) when the valid
    //		state changes on the form as a whole.
  },

  _getState: function (this: any) {
    // summary:
    //		Compute what this.state should be based on state of children
    var states = array.map(this._descendants, function (w: any) {
      return w.get('state') || '';
    });

    return array.indexOf(states, 'Error') >= 0 ? 'Error' :
      array.indexOf(states, 'Incomplete') >= 0 ? 'Incomplete' : '';
  },

  disconnectChildren: function () {
    // summary:
    //		Deprecated method.   Applications no longer need to call this.   Remove for 2.0.
  },

  connectChildren: function (this: any, /*Boolean*/ inStartup: any) {
    // summary:
    //		You can call this function directly, ex. in the event that you
    //		programmatically add a widget to the form *after* the form has been
    //		initialized.

    this._descendants = this._getDescendantFormWidgets();

    // To get notifications from children they need to be started.
    array.forEach(this._descendants, function (child: any) {
      if (!child._started) { child.startup(); }
    });

    if (!inStartup) {
      this._onChildChange();
    }
  },

  _onChildChange: function (this: any, /*String*/ attr: any) {
    // summary:
    //		Called when child's value or disabled state changes

    // The unit tests expect state update to be synchronous, so update it immediately.
    if (!attr || attr == 'state' || attr == 'disabled') {
      this._set('state', this._getState());
    }

    // Use defer() to collapse value changes in multiple children into a single
    // update to my value.
    if (!attr || attr == 'value' || attr == 'disabled' || attr == 'checked') {
      if (this._onChangeDelayTimer) {
        this._onChangeDelayTimer.remove();
      }
      this._onChangeDelayTimer = this.defer(function (this: any) {
        delete this._onChangeDelayTimer;
        this._set('value', this.get('value'));
      }, 10);
    }
  },

  startup: function (this: any) {
    this.inherited(arguments);

    // Set initial this.value and this.state.   Don't emit watch() notifications.
    this._descendants = this._getDescendantFormWidgets();
    this.value = this.get('value');
    this.state = this._getState();

    // Initialize value and valid/invalid state tracking.
    var self = this;
    this.own(
      on(
        this.containerNode,
        'attrmodified-state, attrmodified-disabled, attrmodified-value, attrmodified-checked',
        function (evt: any) {
          if (evt.target == self.domNode) {
            return;	// ignore events that I fire on myself because my children changed
          }
          self._onChildChange(evt.type.replace('attrmodified-', ''));
        }
      )
    );

    // Make state change call onValidStateChange(), will be removed in 2.0
    this.watch('state', function (this: any, attr: any, oldVal: any, newVal: any) { this.onValidStateChange(newVal == ''); });
  },

  destroy: function (this: any) {
    this.inherited(arguments);
  }

});
